'use client';
import { EnergyType } from '@energy-retailer/constants';
import { NavigationCross, OperationsSearch } from '@energy-retailer/icons';
import { useState } from 'react';

import { useDistributorLookup } from '@/app/lib/hooks/use-distributor-lookup';
import { InfoBox, InfoTag } from '@/components/alert';
import { DistributorListing } from '@/components/distributor-listing';
import { Loader } from '@/components/loader';
import { Text } from '@/components/text';
import { RESIDENTIAL_SERVICE_PHONE_NO } from '@/constants';

export const FaultsEmergencyLookup = () => {
  const [postcode, setPostcode] = useState('');
  const { data, isLoading } = useDistributorLookup(postcode);

  const gasDistributors =
    data?.items?.filter(
      (distributor) => distributor.energyType === EnergyType.Gas
    ) || [];
  const electricityDistributors =
    data?.items?.filter(
      (distributor) => distributor.energyType === EnergyType.Electricity
    ) || [];

  return (
    <>
      <div className="bg-gray-50 pb-i pt-d">
        <div className="container">
          <div className="flex items-center justify-center">
            <div className="flex h-full grow-0 flex-col items-center gap-y-a rounded-rad-b bg-white p-d md:p-c lg:px-c lg:py-b">
              <label
                className="text-body-3 block font-semibold"
                htmlFor="postcode"
              >
                Enter Postcode
              </label>
              <div className="align-center flex w-[6.875rem] border-b-2 border-pink pb-a md:w-[7.5rem] lg:w-[7.625rem]">
                <input
                  placeholder="3000"
                  id="postcode"
                  name="postcode"
                  type="text"
                  value={postcode}
                  onChange={(e) => {
                    const regex = /(?!^\+)[^\d]+/g;
                    e.target.value = e.target.value.trim().replace(regex, '');
                    setPostcode(e.target.value);
                  }}
                  maxLength={4}
                  className="text-h2 m-0 w-full border-none bg-transparent p-0 leading-none placeholder:text-gray-200 focus:shadow-none focus:outline-none focus:ring-0 disabled:pointer-events-none disabled:text-gray-200"
                />
                {postcode ? (
                  <button
                    onClick={() => setPostcode('')}
                    type="button"
                    aria-label="Clear postcode"
                  >
                    <NavigationCross className="h-icon-xs w-icon-xs shrink-0 text-pink" />
                  </button>
                ) : (
                  <OperationsSearch
                    className="h-icon-xs w-icon-xs shrink-0 text-pink"
                    data-testid="search-glass"
                  />
                )}
              </div>
            </div>
          </div>
          {data?.error ? (
            <div className="grid grid-cols-5">
              <div className="col-span-5 mt-d flex justify-center">
                <InfoTag>
                  <Text
                    as="p"
                    variant="body2"
                  >
                    Sorry, we don&apos;t currently service the postcode you
                    entered
                  </Text>
                </InfoTag>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div className="container pb-i pt-e">
        {isLoading && postcode.length === 4 ? <Loader /> : null}
        {data?.items && data.items.length >= 1 ? (
          <div className="grid grid-cols-1 gap-y-d lg:grid-cols-6">
            {electricityDistributors.length >= 2 ||
            gasDistributors.length >= 2 ? (
              <div className="col-span-1 lg:col-span-4 lg:col-start-2">
                <InfoBox>
                  <Text
                    as="p"
                    variant="body2"
                  >
                    Looks like your postcode is over multiple distributor areas.
                    You can find your distributor on your energy bill. If
                    you&apos;re not sure who your distributor is, please call us
                    on{' '}
                    <a href={`tel:${RESIDENTIAL_SERVICE_PHONE_NO}`}>
                      <b>{RESIDENTIAL_SERVICE_PHONE_NO}</b>
                    </a>{' '}
                    so we can help you find it.
                  </Text>
                </InfoBox>
              </div>
            ) : null}
            {electricityDistributors.length ? (
              <div className="col-span-1 lg:col-span-4 lg:col-start-2">
                <DistributorListing
                  title={EnergyType.Electricity}
                  distributors={electricityDistributors}
                />
              </div>
            ) : null}
            {gasDistributors.length ? (
              <div className="col-span-1 lg:col-span-4 lg:col-start-2">
                <DistributorListing
                  title={EnergyType.Gas}
                  distributors={gasDistributors}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
};
